"use client";

import NextLink from "next/link";
import React, { useEffect, useState } from "react";
import { cn } from "../../../helpers/classNames";
import { getLinkPartsFromSlug } from "../../../helpers/getLinkPartsFromSlug";
import { getWindow } from "../../../helpers/getWindow";
import { getRoute } from "../../../helpers/urls/getRoute";
import { useCountry } from "../../../hooks/useCountry";
import { usePageChanged } from "../../../hooks/useIsPageChange";
import { useLocationSummary } from "../../../network/matlocq/queries/LocationSummary";
import { ItemInner } from "./itemInner";
import styles from "./styles.module.scss";

type Props = {
    code: string;
    totalAmount: number;
};

export const FavoriteItem: React.FC<Props> = ({
    code,
    totalAmount = 1,
}: Props) => {
    const window = getWindow();
    const { data: favorite } = useLocationSummary(code);
    const [isLoading, setIsLoading] = useState(false);
    const country = useCountry();

    useEffect(() => {
        const listener = (): void => setIsLoading(false);

        // Use pageshow event for iOS devices, which restore a page from the browser history.
        window?.addEventListener("pageshow", listener);
        return (): void => {
            window?.removeEventListener("pageshow", listener);
        };
    }, [window]);

    usePageChanged(() => {
        requestAnimationFrame(() => setIsLoading(false));
    });

    if (
        !favorite?.location ||
        !favorite.location.slug ||
        !favorite.location.code
    ) {
        return null;
    }
    const locCode = favorite.location.code;
    const { seoString } = getLinkPartsFromSlug(favorite.location.slug);
    return (
        <div
            className={cn(
                styles.item,
                totalAmount === 1 && styles.width100,
                totalAmount === 2 && styles.width50,
                totalAmount >= 3 && styles.width40
            )}
        >
            {["at", "pl"].includes(country) ? (
                <NextLink
                    href={getRoute({
                        parameters: {
                            pageName: "forecastToday",
                            seoString,
                            code: locCode,
                        },
                        country,
                    })}
                    data-label="Header_Favoriten"
                    aria-label={favorite.location.name ?? ""}
                >
                    <ItemInner favorite={favorite} totalAmount={totalAmount} />
                </NextLink>
            ) : (
                <a
                    href={`/${favorite.location.slug}`}
                    data-label="Header_Favoriten"
                    onClick={(): void => setIsLoading(true)}
                    aria-label={favorite.location.name ?? ""}
                >
                    <ItemInner
                        favorite={favorite}
                        totalAmount={totalAmount}
                        isLoading={isLoading}
                    />
                </a>
            )}
        </div>
    );
};
